// event.ts ↔ game/event.cpp — the client EventMgr (gevm): posted-event queue,
// peek-keep, pen-hold synthesis, flick history, redraw flags and paint-FPS
// pacing. The C++ GetEvent BLOCKS in HostGetEvent; a browser can't block, so
// the host seams are injected and the shell pumps GetEvent(ctWait=0) per
// animation frame — the function body is a faithful transliteration of the
// C++ loop, which with ctWait=0 runs exactly one pass (events → pen hold →
// timers → paint → timeout), the natural non-blocking pump.

import type { TimerMgr } from './timer.ts';

// ht.h:1813-1824
export interface Event {
  idf: number;
  eType: number;
  chr: number;
  x: number;
  y: number;
  dw: number;
  ff: number;
  ms: number;
}

export const newEvent = (eType = 0): Event => ({
  idf: 0,
  eType,
  chr: 0,
  x: 0,
  y: 0,
  dw: 0,
  ff: 0,
  ms: 0,
});
const copyEvent = (dst: Event, src: Event): void => {
  dst.idf = src.idf;
  dst.eType = src.eType;
  dst.chr = src.chr;
  dst.x = src.x;
  dst.y = src.y;
  dst.dw = src.dw;
  dst.ff = src.ff;
  dst.ms = src.ms;
};
const cloneEvent = (src: Event): Event => {
  const e = newEvent();
  copyEvent(e, src);
  return e;
};

// ht.h:1825-1827
export const kfEvtFinger = 0x00000001;
export const kfEvtCoalesce = 0x00000002;
export const kfEvtRightButton = 0x00000004;

// game/sdl/htplatform.h:43-50 (host events) + ht.h:1831-1853 (custom)
export const penDownEvent = 1;
export const penUpEvent = 2;
export const penMoveEvent = 3;
export const keyDownEvent = 4;
export const penDownEvent2 = 5;
export const penUpEvent2 = 6;
export const penMoveEvent2 = 7;
export const appStopEvent = 22;
export const gamePaintEvent = 0x6000;
export const penHoldEvent = 0x6001;
export const gameOverEvent = 0x6002;
export const penHoverEvent = 0x6003;
export const updateTriggersEvent = 0x6004;
export const gameSuspendEvent = 0x6005;
export const gameResumeEvent = 0x6006;
export const resizeEvent = 0x6007;
export const hidePinEvent = 0x6008;
export const cancelModeEvent = 0x6009;
export const nullEvent = 0x600a;
export const transportEvent = 0x600b;
export const runUpdatesNowEvent = 0x600c;
export const askStringEvent = 0x600d;
export const mpEndMissionWinEvent = 0x600e;
export const mpEndMissionLoseEvent = 0x600f;
export const mpShowObjectivesEvent = 0x6010;
export const checkGameOverEvent = 0x6011;
export const connectionCloseEvent = 0x6012;
export const showMessageEvent = 0x6013;
export const enableSoundEvent = 0x6014;
export const disableSoundEvent = 0x6015;
// ht.h:1853 — WI_AUTOMATION: synthetic control activation (Event::x = control id)
export const wiAutoSelectEvent = 0x6016;

// ht.h:1856-1865 — gameOverEvent constants (placed in Event::dw)
export const knGoSuccess = 0;
export const knGoFailure = 1;
export const knGoInitFailure = 2;
export const knGoAbortLevel = 3;
export const knGoRetryLevel = 4;
export const knGoAppStop = 5;
export const knGoLoadSavedGame = 6;

// ht.h:1866 — modifier placed in appStopEvent's Event::dw; signals a real app exit (the
// Windows WM_CLOSE path) so the loop does NOT save a resume slot.
export const knAppExit = 0xbeeffeed;

// ht.h:1868-1885
export const kcevtPostMax = 20;
export const kfRedrawDirty = 0x01;
export const kfRedrawMax = 0x02;
export const kfRedrawBeforeTimer = 0x04;
export const kfRedrawBeforeInput = 0x08;
export const kfRedrawPaintSkipped = 0x10;
const kcevtPenHistory = 32; // power of 2
const kcmsFlickQuantum = 150;
const kctPenHoldDelay = 50; // event.cpp:31 TUNE

export interface FlickVector {
  dx: number;
  dy: number;
  cms: number;
}

/** The host/presentation seams the C++ reaches via globals. */
export interface EventHost {
  /** HostGetEvent: dequeue one host input event (DOM-fed); ctWait is advisory —
   *  a browser host never blocks, it returns null when its queue is empty. */
  hostGetEvent(ctWait: number): Event | null;
  /** HostGetMillisecondCount */
  clockMs(): number;
  /** gpmfrmm->CookEvent — FormMgr event cooking (focus/capture routing). */
  cookEvent(evt: Event): void;
  /** gpmfrmm->CheckSetRedrawDirty */
  checkSetRedrawDirty(): void;
  /** gsndm.FilterSleepTicks — audio service can shorten waits (identity ok). */
  filterSleepTicks(ct: number): number;
}

export class EventMgr {
  private m_fPeekKeep = false;
  private m_evtPeek: Event = newEvent();
  private m_aevtQ: Event[] = [];
  private m_fAppStopping = false;
  private m_wfRedraw = 0;
  private m_fTimingPenHold = false;
  private m_tPenDown = 0;
  private m_xHold = 0;
  private m_yHold = 0;
  private m_ctMoveEventInterval = 0;
  private m_nctMoveEventFraction = 0;
  private m_fPenHistoryInitialized = false;
  private m_aevtPen1History: Event[] = [];
  private m_aevtPen2History: Event[] = [];
  private m_ievtPen1Next = 0;
  private m_ievtPen2Next = 0;
  /** gcmsNextPaint (event.cpp:6) — absolute ms of the next allowed paint. */
  private gcmsNextPaint = 0;
  /** gcmsDisplayUpdate (game.cpp:78) — minimum ms between paints. */
  gcmsDisplayUpdate = 8;

  private readonly host: EventHost;
  private readonly timm: TimerMgr;

  constructor(host: EventHost, timm: TimerMgr) {
    this.host = host;
    this.timm = timm;
  }

  Init(): void {
    this.m_fPeekKeep = false;
    this.m_aevtQ.length = 0;
    this.m_fAppStopping = false;
    this.m_wfRedraw = 0;
    this.m_fTimingPenHold = false;
    this.m_ctMoveEventInterval = 0;
    this.m_nctMoveEventFraction = 0;
    this.m_fPenHistoryInitialized = false;
  }

  SetAppStopping(): void {
    this.m_fAppStopping = true;
  }
  IsAppStopping(): boolean {
    return this.m_fAppStopping;
  }
  SetRedrawFlags(wf: number): void {
    this.m_wfRedraw |= wf;
  }
  ClearRedrawFlags(wf: number): void {
    this.m_wfRedraw &= ~wf;
  }
  GetRedrawFlags(): number {
    return this.m_wfRedraw;
  }

  // game/event.cpp:17-29
  PeekEvent(pevt: Event, ctWait = 0): boolean {
    if (this.m_fPeekKeep) {
      copyEvent(pevt, this.m_evtPeek);
      return true;
    }
    if (!this.GetEvent(pevt, ctWait)) return false;
    this.m_fPeekKeep = true;
    copyEvent(this.m_evtPeek, pevt);
    return true;
  }

  // game/event.cpp:34-311 — the pump. With ctWait=0 (the browser shell's mode)
  // the loop makes one full pass and returns false on no work.
  /** fSuppressPaint — paint coalescing for a multi-tick catch-up drain
   *  (GameSession.advanceTicks): CheckPaintFPS paces on the SIM clock (ticks*10),
   *  so every tick of an n-tick drain passes the gate and composites a full frame
   *  — while the embedder presents only ONCE per rAF, discarding all but the
   *  last. When set, the paint arm skips producing gamePaintEvent (and leaves
   *  kfRedrawDirty armed + the paint pacing untouched) so the drain's FINAL tick
   *  issues the one real paint. Render-only: no sim/timer behavior changes. */
  GetEvent(pevt: Event, ctWait = -1, fCheckPaints = true, fSuppressPaint = false): boolean {
    copyEvent(pevt, newEvent());

    // force stopping once started
    if (this.m_fAppStopping) {
      if (this.m_fPeekKeep && this.m_evtPeek.eType === appStopEvent) {
        copyEvent(pevt, this.m_evtPeek);
      } else {
        pevt.eType = appStopEvent;
      }
      return true;
    }

    const tStart = this.timm.GetTickCount();
    let ctTimerNext = 0;

    if (this.m_fPeekKeep) {
      copyEvent(pevt, this.m_evtPeek);
      this.m_fPeekKeep = false;
      return true;
    }

    const ctTotal = ctWait;
    let ctElapsed = 0;
    let tCurrent = tStart;
    while (true) {
      // posted events first
      if (this.m_aevtQ.length !== 0) {
        copyEvent(pevt, this.m_aevtQ.shift()!);
        if (pevt.eType === appStopEvent) this.m_fAppStopping = true;
        return true;
      }

      // the shorter of timer wait and asked wait
      let ctNext: number;
      if (ctWait === -1) {
        ctNext = ctTimerNext;
      } else {
        ctNext = ctTotal - ctElapsed;
        if (ctNext < 0) ctNext = 0;
        if (ctTimerNext !== -1 && ctTimerNext < ctNext) ctNext = ctTimerNext;
      }

      // never wait past a pending penHoldEvent
      if (this.m_fTimingPenHold) {
        let ctPenHoldRemaining = kctPenHoldDelay - (tCurrent - this.m_tPenDown);
        if (ctPenHoldRemaining < 0) ctPenHoldRemaining = 0;
        if (ctNext === -1 || ctPenHoldRemaining < ctNext) ctNext = ctPenHoldRemaining;
      }

      if (ctNext !== 0) ctNext = this.host.filterSleepTicks(ctNext);

      // pending-paint bookkeeping before a long sleep (event.cpp:151-168)
      if (this.m_wfRedraw & kfRedrawPaintSkipped && ctWait === -1) {
        const cmsToPaint = this.gcmsNextPaint - this.host.clockMs();
        const ctNextPaint = cmsToPaint > 0 ? Math.trunc(cmsToPaint / 10) : 0;
        tCurrent = this.timm.GetTickCount();
        ctElapsed = tCurrent - tStart;
        ctWait = ctElapsed + ctNextPaint;
        this.m_wfRedraw &= ~kfRedrawPaintSkipped;
        this.m_wfRedraw |= kfRedrawDirty;
      }

      // host events; coalesced pen events feed history then get skipped
      let fHaveEvent = false;
      while (true) {
        const e = this.host.hostGetEvent(this.m_wfRedraw & kfRedrawDirty ? 0 : ctNext);
        if (e === null) {
          fHaveEvent = false;
          break;
        }
        copyEvent(pevt, e);
        fHaveEvent = true;
        this.UpdatePenHistory(pevt);
        if (!(pevt.ff & kfEvtCoalesce)) break;
        ctNext = 0;
      }

      // penHoldEvent production (event.cpp:196-241)
      if (fHaveEvent) {
        switch (pevt.eType) {
          case penDownEvent:
            this.m_fTimingPenHold = true;
            this.m_tPenDown = this.timm.GetTickCount();
            this.m_xHold = pevt.x;
            this.m_yHold = pevt.y;
            break;
          case penMoveEvent:
            if (this.m_fTimingPenHold) {
              if (Math.abs(pevt.x - this.m_xHold) > 7 || Math.abs(pevt.y - this.m_yHold) > 7) {
                this.m_fTimingPenHold = false;
              }
            }
            break;
          case penUpEvent:
            this.m_fTimingPenHold = false;
            break;
        }
      } else {
        // hold events only when nothing else is queued
        if (this.m_fTimingPenHold && tCurrent - this.m_tPenDown >= kctPenHoldDelay) {
          this.m_fTimingPenHold = false;
          fHaveEvent = true;
          pevt.eType = penHoldEvent;
          pevt.idf = 0;
          pevt.x = this.m_xHold;
          pevt.y = this.m_yHold;
        }
      }

      // timers (unless a redraw is racing the timer)
      tCurrent = this.timm.GetTickCount();
      if (
        (this.m_wfRedraw & (kfRedrawDirty | kfRedrawBeforeTimer)) !==
        (kfRedrawDirty | kfRedrawBeforeTimer)
      ) {
        ctTimerNext = this.timm.ScanDispatch(tCurrent);
      }

      if (fHaveEvent) {
        this.host.cookEvent(pevt);
        switch (pevt.eType) {
          case appStopEvent:
            this.m_fAppStopping = true;
            break;
          case penMoveEvent:
          // (m_tLastMoveEvent only throttles 68K hosts)
          // fall through
          case penDownEvent:
            this.m_wfRedraw |= kfRedrawBeforeInput;
            break;
        }
        return true;
      }

      if (fCheckPaints) this.host.checkSetRedrawDirty();

      // paint time? A paint OPPORTUNITY always releases the ordering gates (a
      // suppressed one must not stall timer dispatch — see the ScanDispatch gate
      // above); a suppressed tick additionally leaves kfRedrawDirty armed and
      // skips CheckPaintFPS, so pacing state is unaffected by coalesced ticks.
      if (this.m_wfRedraw & (kfRedrawDirty | kfRedrawMax)) {
        this.m_wfRedraw &= ~(kfRedrawBeforeTimer | kfRedrawBeforeInput);
        if (!fSuppressPaint) {
          if (!(this.m_wfRedraw & kfRedrawMax)) this.m_wfRedraw &= ~kfRedrawDirty;
          if (this.CheckPaintFPS()) {
            this.m_wfRedraw &= ~kfRedrawPaintSkipped;
            pevt.eType = gamePaintEvent;
            return true;
          }
          this.m_wfRedraw |= kfRedrawPaintSkipped;
        }
      }

      // user timeout
      tCurrent = this.timm.GetTickCount();
      ctElapsed = tCurrent - tStart;
      if (ctWait !== -1) {
        if (ctElapsed >= ctWait) return false;
      }
    }
  }

  // game/event.cpp:313-348
  private UpdatePenHistory(pevt: Event): void {
    let fPenEvent = false;
    switch (pevt.eType) {
      case penDownEvent:
      case penMoveEvent:
      case penUpEvent:
        fPenEvent = true;
        if (this.m_fPenHistoryInitialized) {
          this.m_aevtPen1History[this.m_ievtPen1Next] = cloneEvent(pevt);
          this.m_ievtPen1Next = (this.m_ievtPen1Next + 1) & (kcevtPenHistory - 1);
        }
        break;
      case penDownEvent2:
      case penMoveEvent2:
      case penUpEvent2:
        fPenEvent = true;
        if (this.m_fPenHistoryInitialized) {
          this.m_aevtPen2History[this.m_ievtPen2Next] = cloneEvent(pevt);
          this.m_ievtPen2Next = (this.m_ievtPen2Next + 1) & (kcevtPenHistory - 1);
        }
        break;
    }
    if (fPenEvent && !this.m_fPenHistoryInitialized) {
      for (let n = 0; n < kcevtPenHistory; n++) {
        this.m_aevtPen1History[n] = cloneEvent(pevt);
        this.m_aevtPen2History[n] = cloneEvent(pevt);
      }
      this.m_ievtPen1Next = 0;
      this.m_ievtPen2Next = 0;
      this.m_fPenHistoryInitialized = true;
    }
  }

  // game/event.cpp:350-365
  GetFlickVector(nPen: number, pfliv: FlickVector): boolean {
    pfliv.dx = 0;
    pfliv.dy = 0;
    pfliv.cms = 0;
    const msNow = this.host.clockMs();
    const ptStart = { x: 0, y: 0 };
    if (!this.QueryPenHistory(nPen, msNow - kcmsFlickQuantum, ptStart)) return false;
    const ptEnd = { x: 0, y: 0 };
    if (!this.QueryPenHistory(nPen, msNow, ptEnd)) return false;
    pfliv.dx = ptEnd.x - ptStart.x;
    pfliv.dy = ptEnd.y - ptStart.y;
    pfliv.cms = kcmsFlickQuantum;
    return true;
  }

  // game/event.cpp:367-427 — point in pen history at time ms (lerp between
  // bracketing events; PRESENTATION float — flick is gesture chrome)
  private QueryPenHistory(nPen: number, ms: number, ppt: { x: number; y: number }): boolean {
    if (!this.m_fPenHistoryInitialized) return false;

    let aevt: Event[];
    let ievtFirst: number, ievtLast: number;
    if (nPen === 1) {
      aevt = this.m_aevtPen1History;
      ievtFirst = this.m_ievtPen1Next;
      ievtLast = (this.m_ievtPen1Next - 1) & (kcevtPenHistory - 1);
    } else if (nPen === 2) {
      aevt = this.m_aevtPen2History;
      ievtFirst = this.m_ievtPen2Next;
      ievtLast = (this.m_ievtPen2Next - 1) & (kcevtPenHistory - 1);
    } else {
      return false;
    }

    let pevtOnOrBefore: Event | null = null;
    let pevtOnOrAfter: Event | null = null;
    for (let ievt = ievtFirst; ; ievt = (ievt + 1) & (kcevtPenHistory - 1)) {
      const pevt = aevt[ievt];
      if (pevt.ms === ms) {
        pevtOnOrBefore = pevt;
        pevtOnOrAfter = null;
        break;
      }
      if (pevt.ms < ms) pevtOnOrBefore = pevt;
      if (pevt.ms > ms) {
        pevtOnOrAfter = pevt;
        break;
      }
      if (ievt === ievtLast) break;
    }

    if (pevtOnOrBefore === null && pevtOnOrAfter !== null) {
      ppt.x = pevtOnOrAfter.x;
      ppt.y = pevtOnOrAfter.y;
      return true;
    }
    if (pevtOnOrBefore !== null && pevtOnOrAfter === null) {
      ppt.x = pevtOnOrBefore.x;
      ppt.y = pevtOnOrBefore.y;
      return true;
    }
    const flPercent = (ms - pevtOnOrBefore!.ms) / (pevtOnOrAfter!.ms - pevtOnOrBefore!.ms);
    ppt.x =
      pevtOnOrBefore!.x + Math.trunc(flPercent * (pevtOnOrAfter!.x - pevtOnOrBefore!.x) + 0.5);
    ppt.y =
      pevtOnOrBefore!.y + Math.trunc(flPercent * (pevtOnOrAfter!.y - pevtOnOrBefore!.y) + 0.5);
    return true;
  }

  // game/event.cpp:429-453 — paint pacing against gcmsDisplayUpdate
  private CheckPaintFPS(): boolean {
    const cmsCurrent = this.host.clockMs();
    if (this.gcmsNextPaint === 0) {
      this.gcmsNextPaint = cmsCurrent + this.gcmsDisplayUpdate;
      return true;
    } else if (cmsCurrent >= this.gcmsNextPaint) {
      if (cmsCurrent >= this.gcmsNextPaint + this.gcmsDisplayUpdate * 5) {
        this.gcmsNextPaint = cmsCurrent + this.gcmsDisplayUpdate;
      } else {
        this.gcmsNextPaint += this.gcmsDisplayUpdate;
      }
      return true;
    }
    return false;
  }

  // game/event.cpp:497-521
  PostEvent(pevt: Event, fCoalesce = true): void {
    if (fCoalesce) {
      for (let nT = this.m_aevtQ.length - 1; nT >= 0; nT--) {
        if (this.m_aevtQ[nT].eType === pevt.eType) {
          this.m_aevtQ[nT] = cloneEvent(pevt);
          return;
        }
      }
      if (this.m_fPeekKeep && this.m_evtPeek.eType === pevt.eType) {
        copyEvent(this.m_evtPeek, pevt);
        return;
      }
    }
    if (this.m_aevtQ.length < kcevtPostMax) {
      this.m_aevtQ.push(cloneEvent(pevt));
    }
  }

  PostEventType(eType: number, fCoalesce = true): void {
    this.PostEvent(newEvent(eType), fCoalesce);
  }

  // game/event.cpp:532-553 — pen-event rate limiting (68K throttle; kept for
  // fidelity, only feeds m_ctMoveEventInterval which the web path ignores)
  SetPenEventInterval(ctInterval: number): void {
    if (ctInterval >= 50) return;
    const nctFractionNew = ctInterval << 4;
    if (nctFractionNew > this.m_nctMoveEventFraction) {
      this.m_nctMoveEventFraction = nctFractionNew;
    } else {
      this.m_nctMoveEventFraction -= Math.trunc((this.m_nctMoveEventFraction - nctFractionNew) / 4);
    }
    this.m_ctMoveEventInterval = (this.m_nctMoveEventFraction + 16) >> 5;
  }

  // game/event.cpp:555-566
  ClearAppStopping(): void {
    const evt = newEvent();
    while (this.GetEvent(evt, 0, false)) this.m_fAppStopping = false;
    this.m_fAppStopping = false;
  }
}
